export interface AccentGradient {
  base: string;
  dark: string;
}

// Deliberately a wide-hue rainbow set (not a single-tint ramp like the
// Activities Report palette) — this dashboard is meant to read as vivid and
// distinct per-metric at a glance, not as one branded color family.
export const KPI_GRADIENTS: AccentGradient[] = [
  { base: "#6366f1", dark: "#4338ca" }, // indigo — messages
  { base: "#f59e0b", dark: "#b45309" }, // amber — conversations
  { base: "#ec4899", dark: "#be185d" }, // pink — agent replies
  { base: "#14b8a6", dark: "#0f766e" }, // teal — bot replies
  { base: "#8b5cf6", dark: "#6d28d9" }, // violet — connected bots
];

export const GRID_COLOR = "#e7e5ff";

export const CHANNEL_COLORS: Record<string, string> = {
  Line: "#06C755",
  Facebook: "#1877F2",
  Instagram: "#E1306C",
  Whatsapp: "#25D366",
  WhatsApp: "#25D366",
  Telegram: "#229ED9",
};
export const CHANNEL_FALLBACK = "#8b5cf6";

export const BOT_COLORS: Record<string, string> = {
  engati: "#6366f1",
  lazada: "#f1592a",
  "google chat": "#1a73e8",
  whatsapp: "#25D366",
  facebook: "#1877F2",
  tiktok: "#171123",
  shopee: "#ee4d2d",
};
export const BOT_FALLBACK = "#ec4899";

// Bot "platform" icons — same static assets already used on the Chat
// Settings page (integrationSettings.tsx), so a connected bot reads
// identically in both places instead of falling back to plain initials.
export const PLATFORM_ICONS: Record<string, string> = {
  engati: "/assets/img/platform-icons/Engati.png",
  lazada: "/assets/img/platform-icons/Lazada.png",
  "google chat": "/assets/img/platform-icons/Google-Chat.png",
  tiktok: "/assets/img/platform-icons/TikTok.png",
  shopee: "/assets/img/platform-icons/Shopee.png",
};

// All platform-name lookups below are keyed case/whitespace-insensitively —
// the dashboard API, the tenant-bot API, and the chat-type API each surface
// this name independently and aren't guaranteed to agree on casing.
const normalizePlatformName = (botType: string | undefined | null): string =>
  (botType || "").trim().toLowerCase();

export const getPlatformIcon = (botType: string): string | null =>
  PLATFORM_ICONS[normalizePlatformName(botType)] || null;

// The Engati platform is white-labeled to tenants as "Cloudee Chat Bot" —
// same rename Chat Settings already applies to this exact platform name.
export const getPlatformDisplayName = (botType: string): string =>
  normalizePlatformName(botType) === "engati" ? "Cloudee Chat Bot" : botType;

// Google Chat is the only platform used for internal (staff-to-staff) chat;
// every other connected platform talks to external end users.
export const INTERNAL_PLATFORM_NAME = "Google Chat";

export const isInternalPlatform = (botType: string | undefined | null): boolean =>
  normalizePlatformName(botType) === normalizePlatformName(INTERNAL_PLATFORM_NAME);

export const SCOPE_COLORS = {
  external: "#8b5cf6",
  internal: "#1a73e8",
};

// Vivid categorical set for series/segments (agents, teams, generic bars).
export const CATEGORICAL_PALETTE = [
  "#6366f1", // indigo
  "#f59e0b", // amber
  "#ec4899", // pink
  "#14b8a6", // teal
  "#8b5cf6", // violet
  "#22c55e", // green
  "#ef4444", // red
  "#0ea5e9", // sky
];

export const SOURCE_COLORS = {
  agent: "#f59e0b",
  bot: "#14b8a6",
  customer: "#8b5cf6",
};

export const getChannelColor = (channel: string): string =>
  CHANNEL_COLORS[channel] || CHANNEL_FALLBACK;

export const getBotColor = (botType: string): string =>
  BOT_COLORS[normalizePlatformName(botType)] || BOT_FALLBACK;

export const renderTooltipCard = (innerHtml: string): string => `
  <div style="background:#ffffff;color:#171123;border:1px solid rgba(99,102,241,0.14);box-shadow:0 12px 30px -10px rgba(99,102,241,0.35);border-radius:12px;padding:10px 14px;min-width:170px;">
    ${innerHtml}
  </div>
`;

export const renderTooltipHeader = (
  label: string,
  count: number,
  swatch?: string
): string => `
  <div style="display:flex;align-items:center;gap:8px;font-weight:700;font-size:13px;color:#171123;">
    ${
      swatch
        ? `<span style="width:10px;height:10px;border-radius:50%;background:${swatch};display:inline-block;"></span>`
        : ""
    }
    ${label} · ${count}
  </div>
`;

export const renderTooltipRow = (
  label: string,
  count: number,
  swatch?: string,
  emphasize?: boolean
): string => `
  <div style="display:flex;align-items:center;justify-content:space-between;gap:16px;font-size:12px;color:#655f78;margin-top:4px;${
    emphasize
      ? "background:rgba(139,102,246,0.1);border-radius:6px;padding:3px 6px;margin-left:-6px;margin-right:-6px;"
      : ""
  }">
    <span style="display:flex;align-items:center;gap:6px;${emphasize ? "font-weight:700;" : ""}">
      ${
        swatch
          ? `<span style="width:8px;height:8px;border-radius:2px;background:${swatch};display:inline-block;"></span>`
          : ""
      }
      ${label}
    </span>
    <strong style="color:#171123;">${count}</strong>
  </div>
`;
